import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, computed, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import { sortBy } from 'lodash';
import { BehaviorSubject, combineLatest, filter, Subscription } from 'rxjs';
import { IDateRangePickerDate } from '../../../../shared/components/date-range-picker/date-range-picker.interface';
import { UserEffects } from '../../../../state/user/user.effects';
import { IItemsFilterCardFields } from '../../items-filter-card/items-filter-card.interface';
import { ItemsViewControlsComponent } from '../../items-view-controls/items-view-controls.component';
import { EWorkflowIcon, IBoardTreeStructure, IItemJoins } from '../../items.interface';
import { ItemsService } from '../../items.service';
import { IKamishibaiColumn, IKamishibaiItem, ITableRow, TKamishibaiTableDateGroup } from './items-kamishibai.interface';
import { IKamishibaiViewStore } from './items-kamishibai.store';
import { KamishibaiCardComponent } from './kamishibai-card/kamishibai-card.component';

const ZOOM_STORAGE_KEY = 'kamishibaiZoomLevel';

@Component({
  imports: [
    MatTableModule,
    NgForOf,
    NgClass,
    KamishibaiCardComponent,
    NgIf,
    TranslatePipe,
    MatIcon,
    MatIconButton,
    MatTooltip,
    ItemsViewControlsComponent,
  ],
  providers: [IKamishibaiViewStore],
  selector: 'app-items-kamishibai',
  standalone: true,
  styleUrls: ['items-kamishibai.component.scss'],
  templateUrl: 'items-kamishibai.component.html',
})
export class ItemsKamishibaiComponent implements OnInit, OnDestroy {
  protected readonly subscriptions: Subscription[] = [];

  @Input({ required: true }) public filterCardData$ = new BehaviorSubject<Partial<IItemsFilterCardFields> | null>(null);
  @Input() public boardTreeStructure: IBoardTreeStructure[] = [];

  @Output() public dateRangeChange = new EventEmitter<IDateRangePickerDate>();

  private readonly userTimezone: string;
  // The raw, possibly-absent filter value — only ever written to. Read `effectiveDateRange` below,
  // never this directly; it doesn't carry the "no filter → fall back to this month" fallback.
  private readonly dateRange = signal<IDateRangePickerDate | null | undefined>(undefined);
  // The one place the "no filter → fall back to this month" rule (`ItemsService.getEffectiveDateRange`,
  // shared with kanban's query building) is read from — every board-layout/query call site uses this
  // instead of re-deriving it.
  private readonly effectiveDateRange = computed(() => this.itemsService.getEffectiveDateRange(this.dateRange()));
  private displayedRange: { endDate: Dayjs; startDate: Dayjs } | null = null;

  public initialData: IKamishibaiItem[] = [];
  public items: IKamishibaiItem[] = [];

  public uniqueDates: string[] = [];
  public displayedColumns: string[] = [];
  public dateColumns: IKamishibaiColumn[] = [];
  public dataSource: ITableRow[] = [];

  public readonly legendItems: { icon: string; key: string; labelKey: string; hintKey?: string }[] = [
    { icon: EWorkflowIcon.adjust, key: 'todo', labelKey: 'system.label.todo' },
    { icon: EWorkflowIcon.schedule, key: 'inProgress', labelKey: 'system.label.inProgress' },
    { icon: EWorkflowIcon.task_alt, key: 'done', labelKey: 'system.label.done' },
    {
      hintKey: 'page.items.kamishibai.overdueHint',
      icon: 'error_outline',
      key: 'overdue',
      labelKey: 'page.items.label.overdue',
    },
    {
      hintKey: 'page.items.kamishibai.overdueHint',
      icon: EWorkflowIcon.schedule,
      key: 'inProgressOverdue',
      labelKey: 'page.items.kamishibai.inProgressOverdue',
    },
  ];
  public isSimpleCardMode = false;
  public monthText: string | undefined;
  public dayFormat = 'YYYY-MM-DD';
  // Assigned in the constructor: field initialisers run before `userTimezone` is set,
  // so computing it here would resolve "today" against the browser timezone instead.
  public today: string;

  public readonly minZoom = 0.4;
  public readonly maxZoom = 1.5;
  private readonly zoomStep = 0.1;
  public zoomLevel: number = this.readStoredZoom();
  public zoomPercent: number = Math.round(this.zoomLevel * 100);

  constructor(
    public readonly store: IKamishibaiViewStore,
    public readonly itemsService: ItemsService,
  ) {
    this.userTimezone = UserEffects.getUser().configuration.timezone;
    this.today = this.getToday();
    this.prepareDataSource(this.items);
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.store.data$.subscribe((data: IItemJoins<string>[]) => {
        this.initialData = this.prepareBoardInitData();

        this.items = [];

        for (const item of data) {
          const formattedData: IKamishibaiItem | null = this.store.formatData(item);

          if (formattedData) {
            this.items.push(formattedData);
          }
        }

        this.prepareDataSource(this.items);
      }),
      combineLatest({
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      }).subscribe(({ filterCard }) => {
        if (filterCard.itemView !== 'kamishibai') {
          return;
        }

        this.dateRange.set(filterCard.dateRange);

        if (!this.items?.length) {
          this.prepareWithInitData();
        }

        this.loadItems(this.filterCardData$.value as IItemsFilterCardFields);
      }),

      this.itemsService.refreshItems$.subscribe(() => {
        this.loadItems(this.filterCardData$.value as IItemsFilterCardFields);
      }),
    );
  }

  public refreshDashboard(): void {
    this.itemsService.triggerRefresh();
  }

  public zoomIn(): void {
    this.setZoom(this.zoomLevel + this.zoomStep);
  }

  public zoomOut(): void {
    this.setZoom(this.zoomLevel - this.zoomStep);
  }

  private setZoom(value: number): void {
    // Clamp and round to one decimal so repeated steps don't drift (0.1 + 0.2 issues).
    const clamped: number = Math.min(this.maxZoom, Math.max(this.minZoom, Math.round(value * 10) / 10));

    this.zoomLevel = clamped;
    this.zoomPercent = Math.round(clamped * 100);

    try {
      localStorage.setItem(ZOOM_STORAGE_KEY, String(clamped));
    } catch {
      // Ignore storage failures (e.g. private mode); zoom still works for the session.
    }
  }

  private readStoredZoom(): number {
    const stored = Number(localStorage.getItem(ZOOM_STORAGE_KEY));

    if (!Number.isFinite(stored) || stored < this.minZoom || stored > this.maxZoom) {
      return 1;
    }

    return Math.round(stored * 10) / 10;
  }

  private prepareWithInitData(): void {
    if (this.items?.length) {
      return;
    }

    this.initialData = this.prepareBoardInitData();
    this.prepareDataSource([]);
  }

  private prepareDataSource(formattedItems: IKamishibaiItem[]): void {
    const items: IKamishibaiItem[] = sortBy(
      this.mergeInitialDataWithItems(this.initialData, formattedItems),
      [
        (item: IKamishibaiItem): number => {
          switch (item.group) {
            case 'daily':
              return 0;
            case 'weekly':
              return 1;
            case 'monthly':
              return 2;
            default:
              return 3;
          }
        },
      ],
      ['asc'],
    );

    this.uniqueDates = [...new Set(items.map((item) => item.date))].sort();
    this.displayedColumns = ['group', ...this.uniqueDates];
    this.dateColumns = this.buildDateColumns(this.uniqueDates);

    const uniqueGroups: string[] = [...new Set(items.map((item: IKamishibaiItem): string => item.group))];

    this.dataSource = uniqueGroups.flatMap((groupName: string): ITableRow[] => {
      const groupItems = items.filter((item: IKamishibaiItem): boolean => item.group === groupName);
      const groupCount: number = groupItems.filter((item: IKamishibaiItem): boolean => Boolean(item.data)).length;

      return this.organizeGroupData(groupItems, groupName, groupCount);
    });
  }

  private buildDateColumns(dates: string[]): IKamishibaiColumn[] {
    return dates.map((date: string): IKamishibaiColumn => {
      // `date` is already a calendar date in the user's timezone; re-reading it as UTC (what the
      // dateFormat pipe does) would shift the day for users behind UTC.
      const day: Dayjs = dayjs(date);
      const weekday: number = day.day();

      return {
        date,
        dayNumber: day.format('D'),
        isToday: date === this.today,
        isWeekend: weekday === 0 || weekday === 6,
        weekdayLabel: day.format(this.isSimpleCardMode ? 'ddd' : 'dddd'),
      };
    });
  }

  private organizeGroupData(items: IKamishibaiItem[], groupName: string, groupCount: number): ITableRow[] {
    const dateGroups: TKamishibaiTableDateGroup = items.reduce(
      (acc: TKamishibaiTableDateGroup, item: IKamishibaiItem): TKamishibaiTableDateGroup => {
        if (!acc[item.date]) {
          acc[item.date] = [];
        }

        acc[item.date].push(item);

        return acc;
      },
      {} as TKamishibaiTableDateGroup,
    );

    const maxRows: number = Math.max(...Object.values(dateGroups).map((g: IKamishibaiItem[]): number => g.length));
    const tableData: ITableRow[] = [];

    for (let i = 0; i < maxRows; i++) {
      const row: ITableRow = { group: i === 0 ? groupName : '', groupCount, rowSpan: i === 0 ? maxRows : 0 };

      this.uniqueDates.forEach((date: string): void => {
        const itemsForDate: IKamishibaiItem[] = dateGroups[date] || [];
        row[date] = itemsForDate[i] ? itemsForDate[i].data : null;
      });

      tableData.push(row);
    }

    return tableData;
  }

  private getCroppedStartEndDate(startDate: Dayjs, endDate: Dayjs): [Dayjs, Dayjs] {
    let croppedEndDate: Dayjs = endDate;
    const oneMonthLater = startDate.add(1, 'month').startOf('day');

    if (endDate.isAfter(oneMonthLater)) {
      croppedEndDate = startDate.endOf('month').startOf('day');
    }

    this.displayedRange = { endDate: croppedEndDate, startDate };
    this.monthText = this.getMonthText(startDate, croppedEndDate);

    return [startDate, croppedEndDate];
  }

  private getMonthText(startDate: Dayjs, endDate: Dayjs): string {
    const startMonth: string = startDate.format('MMMM');
    const endMonth: string = endDate.format('MMMM');
    const monthLabel: string = startMonth === endMonth ? startMonth : `${startMonth} - ${endMonth}`;

    if (this.isFullMonth(startDate, endDate)) {
      return monthLabel;
    }

    const dayLabel: string = startDate.isSame(endDate, 'day')
      ? `${startDate.date()}`
      : `${startDate.date()}-${endDate.date()}`;

    return `${monthLabel} (${dayLabel})`;
  }

  private isFullMonth(startDate: Dayjs, endDate: Dayjs): boolean {
    return startDate.isSame(startDate.startOf('month'), 'day') && endDate.isSame(endDate.endOf('month'), 'day');
  }

  public shiftDateRange(direction: 'backward' | 'forward'): void {
    if (!this.displayedRange) {
      return;
    }

    const { startDate, endDate } = this.displayedRange;
    const shift: 'add' | 'subtract' = direction === 'forward' ? 'add' : 'subtract';
    let newRange: IDateRangePickerDate;

    if (this.isFullMonth(startDate, endDate)) {
      const shiftedMonth: Dayjs = startDate[shift](1, 'month');

      newRange = { endDate: shiftedMonth.endOf('month'), startDate: shiftedMonth.startOf('month') };
    } else {
      const displayedDayCount: number = endDate.diff(startDate, 'day') + 1;

      newRange = {
        endDate: endDate[shift](displayedDayCount, 'day').endOf('day'),
        startDate: startDate[shift](displayedDayCount, 'day').startOf('day'),
      };
    }

    // `dateRangeChange` triggers an immediate `patchValue({ dateRange }, true)` on the manager (see
    // `ItemsComponent.onKamishibaiDateRangeChange`), but that's an async round-trip back through
    // `appliedValue`/`filterCardData$` — moving the calendar locally first keeps it in sync with the
    // click itself, with no visible delay.
    this.applyLocalDateRange(newRange);
    this.dateRangeChange.emit(newRange);
  }

  private applyLocalDateRange(dateRange: IDateRangePickerDate): void {
    this.dateRange.set(dateRange);
    this.initialData = this.prepareBoardInitData();
    this.loadItems({ ...(this.filterCardData$.value as IItemsFilterCardFields), dateRange });
  }

  private mergeInitialDataWithItems(initialData: IKamishibaiItem[], items: IKamishibaiItem[]): IKamishibaiItem[] {
    const itemsGrouped = new Map<string, IKamishibaiItem[]>();

    items.forEach((item: IKamishibaiItem): void => {
      const key = `${item.date}_${item.group}`;

      if (!itemsGrouped.has(key)) {
        itemsGrouped.set(key, []);
      }

      itemsGrouped.get(key)?.push(item);
    });

    const result: IKamishibaiItem[] = [];

    initialData.forEach((initialItem: IKamishibaiItem): void => {
      const key = `${initialItem.date}_${initialItem.group}`;
      const matchingItems: IKamishibaiItem[] | undefined = itemsGrouped.get(key);

      if (matchingItems && matchingItems.length > 0) {
        matchingItems.forEach((item) => {
          result.push({
            ...item,
            date: initialItem.date,
            group: initialItem.group,
          });
        });
      } else {
        result.push(initialItem);
      }
    });

    return sortBy(result, 'data.dueDate');
  }

  private prepareBoardInitData(): IKamishibaiItem[] {
    const days: IKamishibaiItem[] = [];
    const dateRange = this.effectiveDateRange();
    const [dateRangeStartDate, dateRangeEndDate] = this.getCroppedStartEndDate(dateRange.startDate, dateRange.endDate);

    let currentDate = dateRangeStartDate.startOf('day');
    let endDate = dateRangeEndDate.startOf('day');

    const oneMonthLater = dateRange.startDate.add(1, 'month').startOf('day');

    if (endDate.isAfter(oneMonthLater)) {
      endDate = dateRange.startDate.endOf('month').startOf('day');
    }

    const diffDays = endDate.diff(currentDate, 'day') + 1;

    this.isSimpleCardMode = diffDays > 7;

    Array.from({ length: diffDays }).forEach((): void => {
      days.push({
        data: null,
        date: currentDate.format(this.dayFormat),
        group: 'daily',
      });
      days.push({
        data: null,
        date: currentDate.format(this.dayFormat),
        group: 'weekly',
      });
      days.push({
        data: null,
        date: currentDate.format(this.dayFormat),
        group: 'monthly',
      });

      currentDate = currentDate.add(1, 'day');
    });

    return days;
  }

  public getToday(): string {
    return dayjs().tz(this.userTimezone).format(this.dayFormat);
  }

  public loadItems(filterCard: IItemsFilterCardFields): void {
    // Keeps `dateRange` authoritative for whatever `filterCard` is actually passed in, rather than
    // relying on some earlier call having already synced it — see `effectiveDateRange` above.
    this.dateRange.set(filterCard.dateRange);

    const effectiveDateRange = this.effectiveDateRange();
    const [dateRangeStartDate, dateRangeEndDate] = this.getCroppedStartEndDate(
      effectiveDateRange.startDate,
      effectiveDateRange.endDate,
    );
    // A new object, never a mutation of `filterCard` (the same reference as `filterCardData$.value`)
    // — the "this month" fallback is local to this one query and must never leak back into the
    // shared filter card state (only the month-navigation arrows are allowed to do that, via
    // `dateRangeChange` → `ItemsComponent.onKamishibaiDateRangeChange`).
    const filterCardForQuery: IItemsFilterCardFields = {
      ...filterCard,
      dateRange: { endDate: dateRangeEndDate, startDate: dateRangeStartDate },
    };

    this.store.loadItems({
      additionalCustomSearch: this.itemsService.prepareAdditionalCustomSearch(
        filterCardForQuery,
        this.boardTreeStructure,
      ),
      fields: ['name', 'key', 'dueDate', 'currentWorkflowStepId'],
      filters: [...this.itemsService.prepareFilters(filterCardForQuery)],
      join: [
        'board||name,key',
        'currentWorkflowStep||name,isDefault,statusCategory',
        'assignee||name',
        'boardItemConfiguration||name,key,itemType',
        'boardItemConfiguration.workflow||id',
        'boardItemConfiguration.workflow.workflowStepAssignments||id',
        'boardItemConfiguration.workflow.workflowStepAssignments.workflowStep||name,isDefault,statusCategory',
        'boardItemConfiguration.workflow.workflowStepAssignments.nextWorkflowStep||name,isDefault,statusCategory',
        'itemRecurrence||cron',
      ],
      limit: 1000,
      page: 1,
    });
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription?.unsubscribe();
    }
  }
}
